<template>
  <!-- Enhanced Header Section -->
  <div
    :class="[
      'bg-white rounded border p-4 md:p-6 mb-6',
      TotalStudents === 0 ? 'mt-8' : '',
    ]"
  >
    <div
      class="flex flex-col-reverse lg:flex-row gap-4 items-start lg:items-center justify-between"
    >
      <!-- Search and Filters Section -->
      <div
        class="flex flex-col sm:flex-row gap-3 md:gap-4 flex-1 w-full lg:w-auto"
      >
        <!-- Search -->
        <div class="flex-1 min-w-0">
          <Search
            @search-text="emitSearchText"
            v-model="searchText"
          />
        </div>
        <!-- Filters Container -->
        <div class="flex flex-col sm:flex-row gap-3 md:gap-4">
          <!-- keep YearPicker mounted to satisfy hook-at-top-level rule; hide via
          wrapper class -->
          <div
            v-if="isPassoutClass"
            class="min-w-0"
          >
            <YearPicker @selected-year="emitSelectedYear" />
          </div>
          <div class="min-w-0">
            <StatusFilter @selected-class="emitSelectedClass" />
          </div>
        </div>
      </div>
      <!-- Enhanced Add Student Button -->
      <button
        @click="showAddModal = true"
        class="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
      >
        <Plus class="w-5 h-5" />
        <span class="font-bold">Add Student</span>
      </button>
    </div>
  </div>

  <!-- Toast Notification -->
  <div
    v-if="showToast"
    class="fixed top-4 right-4 z-[10001] transform transition-all duration-300 ease-in-out"
    :class="
      showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    "
  >
    <div
      class="bg-white rounded-xl shadow-2xl border border-green-200 p-4 max-w-sm"
    >
      <div class="flex items-start gap-3">
        <!-- Success Icon -->
        <div
          class="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
        >
          <CheckCircle class="w-5 h-5 text-green-600" />
        </div>
        <!-- Toast Content -->
        <div class="flex-1 min-w-0">
          <h4 class="text-sm font-semibold text-gray-900">Success!</h4>
          <p class="text-sm text-gray-600 mt-1">Student added successfully</p>
        </div>
        <!-- Close Button -->
        <button
          @click="closeToast"
          class="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
  <!-- Enhanced Scrollable Add Student Modal -->
  <div
    v-if="showAddModal"
    class="fixed inset-0 z-[10000] bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
  >
    <div
      class="bg-white w-full max-w-4xl max-h-[95vh] rounded-3xl shadow-2xl relative animate-fade-in border border-gray-200 flex flex-col"
    >
      <!-- Modal Loading Overlay -->
      <div
        v-if="isAdding"
        class="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center rounded-3xl"
      >
        <div class="text-center">
          <div
            class="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"
          ></div>
          <p class="text-sm text-gray-600">Adding student...</p>
        </div>
      </div>
      <!-- Fixed Header -->
      <div class="flex-shrink-0 p-6 md:p-8 border-b border-gray-200">
        <!-- Close Button -->
        <button
          @click="closeAddModal"
          :disabled="isAdding"
          class="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 disabled:opacity-50"
          title="Close"
        >
          <X class="w-5 h-5" />
        </button>
        <!-- Header -->
        <div class="flex items-center gap-3">
          <div
            class="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg"
          >
            <UserPlus class="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 class="text-xl md:text-2xl font-bold text-gray-800">
              Add New Student
            </h2>
            <p class="text-gray-500 text-sm">
              Fill in the student information and upload documents
            </p>
          </div>
        </div>
      </div>
      <!-- Scrollable Form Content -->
      <div class="flex-1 overflow-y-auto p-6 md:p-8">
        <!-- Error Message -->
        <div
          v-if="generalError"
          class="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
        >
          <div class="p-1 bg-red-100 rounded-full">
            <AlertCircle class="w-4 h-4 text-red-600" />
          </div>
          <div class="flex-1">
            <h4 class="text-sm font-semibold text-red-800">Error</h4>
            <p class="text-sm text-red-700 mt-1">{{ generalError }}</p>
          </div>
          <button
            @click="
              () => {
                closeAddModal();
                generalError = '';
              }
            "
            class="p-1 text-red-400 hover:text-red-600 transition-colors"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
        <div class="space-y-8">
          <!-- Basic Information Section -->
          <div class="bg-gray-50 rounded-xl p-6">
            <h3
              class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"
            >
              <div class="p-1 bg-blue-100 rounded">
                <User class="w-4 h-4 text-blue-600" />
              </div>
              Basic Information
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <!-- Full Name -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-blue-100 rounded">
                    <User class="w-3 h-3 text-blue-600" />
                  </div>
                  Full Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="newStudent.name"
                  :disabled="isAdding"
                  placeholder="Enter student name"
                  :class="[
                    'w-full rounded-xl border-2 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50',
                    formErrors.name
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                />
                <p
                  v-if="formErrors.name"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.name }}
                </p>
              </div>
              <!-- Roll Number -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-purple-100 rounded">
                    <Hash class="w-3 h-3 text-purple-600" />
                  </div>
                  Roll Number <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="newStudent.rollNumber"
                  :disabled="isAdding"
                  placeholder="Enter roll number"
                  :class="[
                    'w-full rounded-xl border-2 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50',
                    formErrors.rollNumber
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                />
                <p
                  v-if="formErrors.rollNumber"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.rollNumber }}
                </p>
              </div>
              <!-- Email -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-green-100 rounded">
                    <Mail class="w-3 h-3 text-green-600" />
                  </div>
                  Email Address
                </label>
                <input
                  v-model="newStudent.email"
                  :disabled="isAdding"
                  type="email"
                  placeholder="student@example.com"
                  :class="[
                    'w-full rounded-xl border-2 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50',
                    formErrors.email
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                />
                <p
                  v-if="formErrors.email"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.email }}
                </p>
              </div>
              <!-- Phone -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-orange-100 rounded">
                    <Phone class="w-3 h-3 text-orange-600" />
                  </div>
                  Student Number<span class="text-red-500">*</span>
                </label>
                <input
                  v-model="newStudent.phone"
                  :disabled="isAdding"
                  placeholder="9876598765"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
                <p
                  v-if="formErrors.phone"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.phone }}
                </p>
              </div>
              <!-- Secondary Phone -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-orange-100 rounded">
                    <Phone class="w-3 h-3 text-orange-600" />
                  </div>
                  Parents Phone
                </label>

                <input
                  v-model="newStudent.secondaryPhone"
                  :disabled="isAdding"
                  placeholder="Emergency / Guardian number"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />

                <p
                  v-if="formErrors.secondaryPhone"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.secondaryPhone }}
                </p>
              </div>

              <!-- Class -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-emerald-100 rounded">
                    <List class="w-3 h-3 text-emerald-600" />
                  </div>
                  Class <span class="text-red-500">*</span>
                </label>

                <select
                  v-model="newStudent.class"
                  :disabled="isAdding"
                  :class="[
                    'w-full rounded-xl border-2 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium bg-white text-sm md:text-base disabled:opacity-50',
                    formErrors.class
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                >
                  <option
                    value=""
                    disabled
                    selected
                    hidden
                  >
                    Select class
                  </option>

                  <option
                    v-for="cls in filteredClasses"
                    :key="cls._id"
                    :value="cls._id"
                  >
                    {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                  </option>
                </select>

                <p
                  v-if="formErrors.class"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.class }}
                </p>
              </div>

              <!-- Status -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-indigo-100 rounded">
                    <Activity class="w-3 h-3 text-indigo-600" />
                  </div>
                  Status<span class="text-red-500">*</span>
                </label>
                <select
                  v-model="newStudent.status"
                  :disabled="isAdding"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium bg-white text-sm md:text-base disabled:opacity-50"
                >
                  <option
                    v-for="status in filteredStatuses"
                    :key="status.value"
                    :value="status.value"
                  >
                    {{ status.label }}
                  </option>
                </select>

                <p
                  v-if="formErrors.status"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.status }}
                </p>
              </div>
              <!-- Session Field - Only show when both class and status are passout -->
              <div
                v-if="
                  isPassoutSelectedInForm && newStudent.status === 'passout'
                "
                class="space-y-2"
              >
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-teal-100 rounded">
                    <Calendar class="w-3 h-3 text-teal-600" />
                  </div>
                  Session <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="newStudent.session"
                  :disabled="isAdding"
                  placeholder="e.g. Winter / Summer"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
                <p
                  v-if="formErrors.session"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.session }}
                </p>
              </div>

              <!-- Year Field - Only show when both class and status are passout -->
              <div
                v-if="
                  isPassoutSelectedInForm && newStudent.status === 'passout'
                "
                class="space-y-2"
              >
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-amber-100 rounded">
                    <Calendar class="w-3 h-3 text-amber-600" />
                  </div>
                  Year <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="newStudent.year"
                  :disabled="isAdding"
                  type="number"
                  min="1900"
                  :max="new Date().getFullYear() + 10"
                  placeholder="e.g., 2024"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
                <p
                  v-if="formErrors.year"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.year }}
                </p>
              </div>
              <!-- Enrollment Date -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-cyan-100 rounded">
                    <Calendar class="w-3 h-3 text-cyan-600" />
                  </div>
                  Enrollment Date<span class="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  v-model="newStudent.enrollmentDate"
                  :disabled="isAdding"
                  :max="new Date().toISOString().split('T')[0]"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
                <p
                  v-if="formErrors.enrollmentDate"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.enrollmentDate }}
                </p>
              </div>
              <!-- Date of Birth -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <div class="p-1 bg-pink-100 rounded">
                    <Calendar class="w-3 h-3 text-pink-600" />
                  </div>
                  Date of Birth
                </label>

                <input
                  type="date"
                  v-model="newStudent.dob"
                  :disabled="isAdding"
                  :max="new Date().toISOString().split('T')[0]"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />

                <p
                  v-if="formErrors.dob"
                  class="text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-4 h-4" />
                  {{ formErrors.dob }}
                </p>
              </div>

              <!-- Address -->
              <!-- State (optional) -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <Map class="w-4 h-4 text-blue-600" />
                  State
                </label>
                <input
                  v-model="newStudent.state"
                  :disabled="isAdding"
                  placeholder="State"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
              </div>

              <!-- District (optional) -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <MapPin class="w-4 h-4 text-purple-600" />
                  District
                </label>
                <input
                  v-model="newStudent.district"
                  :disabled="isAdding"
                  placeholder="District"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
              </div>

              <!-- City / Area (optional) -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <Building2 class="w-4 h-4 text-emerald-600" />
                  City / Area
                </label>
                <input
                  v-model="newStudent.city"
                  :disabled="isAdding"
                  placeholder="City or Area"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
              </div>

              <!-- Pincode (required) -->
              <div class="space-y-2">
                <label
                  class="block text-sm font-semibold text-gray-700 flex items-center gap-2"
                >
                  <Hash class="w-4 h-4 text-red-600" />
                  Pincode <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="newStudent.pincode"
                  :disabled="isAdding"
                  placeholder="6-digit pincode"
                  maxlength="6"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50"
                />
                <p
                  v-if="formErrors.pincode"
                  class="text-sm text-red-600"
                >
                  {{ formErrors.pincode }}
                </p>
              </div>
            </div>
          </div>
          <!-- Photo Section -->
          <div class="bg-blue-50 rounded-xl p-6">
            <h3
              class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"
            >
              <div class="p-1 bg-blue-100 rounded">
                <Camera class="w-4 h-4 text-blue-600" />
              </div>
              Student Photo
            </h3>
            <div class="space-y-4">
              <!-- Current Photo Display -->
              <div
                v-if="newStudent.photo"
                class="flex items-center gap-4"
              >
                <img
                  :src="newStudent.photo"
                  alt="Student Photo"
                  class="w-16 h-16 rounded-lg object-cover border border-gray-300"
                />
                <div>
                  <p class="text-sm font-medium text-gray-700">
                    Photo Selected
                  </p>
                  <button
                    @click="newStudent.photo = ''"
                    :disabled="isAdding"
                    class="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Remove Photo
                  </button>
                </div>
              </div>
              <!-- Photo Upload -->
              <div class="space-y-2">
                <label class="block text-sm font-semibold text-gray-700">
                  Upload Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  @change="handlePhotoUpload"
                  :disabled="isAdding"
                  class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50"
                />
                <p class="text-xs text-gray-500">
                  Upload a clear photo of the student (JPG, PNG, max 1MB - will
                  be automatically compressed)
                </p>
                <p
                  v-if="formErrors.photo"
                  class="text-sm text-red-600"
                >
                  {{ formErrors.photo }}
                </p>
              </div>
            </div>
          </div>
          <!-- Documents Section -->
          <div class="bg-yellow-50 rounded-xl p-6">
            <h3
              class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"
            >
              <div class="p-1 bg-yellow-100 rounded">
                <FileText class="w-4 h-4 text-yellow-600" />
              </div>
              Documents
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <!-- Document Upload Input Component (Reusable) -->
              <template
                v-for="doc in [
                  'aadhaarCard',
                  'birthCertificate',
                  'transferCertificate',
                  'marksheet',
                ]"
                :key="doc"
              >
                <div class="space-y-2">
                  <label
                    class="block text-sm font-semibold text-gray-700 capitalize"
                  >
                    {{ doc.replace(/([A-Z])/g, " $1") }}
                    <span
                      v-if="
                        [
                          'transferCertificate',
                          'marksheet',
                          'aadhaarCard',
                          'birthCertificate',
                        ].includes(doc)
                      "
                      class="text-green-500"
                    >
                      (optional)
                    </span>
                  </label>
                  <!-- If file uploaded -->
                  <div
                    v-if="newStudent[doc]"
                    class="mb-2"
                  >
                    <div
                      class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded"
                    >
                      <CheckCircle class="w-4 h-4 text-green-600" />
                      <span class="text-sm text-green-700"
                        >Document uploaded</span
                      >
                      <button
                        @click="newStudent[doc] = ''"
                        :disabled="isAdding"
                        class="ml-auto text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        <X class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    @change="handleDocumentUpload($event, doc)"
                    :disabled="isAdding"
                    class="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50"
                  />
                  <p
                    v-if="formErrors[doc]"
                    class="text-sm text-red-600"
                  >
                    {{ formErrors[doc] }}
                  </p>
                </div>
              </template>
            </div>
            <p class="text-xs text-gray-500 mt-4">
              Supported formats: PDF, JPG, PNG (Max: 2MB per file - images will
              be compressed)
            </p>
          </div>
        </div>
      </div>
      <!-- Fixed Footer -->
      <div class="flex-shrink-0 p-6 md:p-8 border-t border-gray-200">
        <div
          class="flex flex-col-reverse md:flex-row justify-end gap-3 md:gap-4"
        >
          <button
            @click="closeAddModal"
            :disabled="isAdding"
            class="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all duration-200 transform hover:scale-105 text-sm md:text-base disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="addStudent"
            :disabled="isAdding"
            class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm md:text-base disabled:opacity-50 z-100"
          >
            <div
              v-if="isAdding"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            ></div>
            <Save
              v-else
              class="w-4 h-4"
            />
            <span>{{ isAdding ? "Adding..." : "Save Student" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, computed, onMounted } from "vue";
  import { useStore } from "vuex";

  import {
    UserPlus,
    X,
    Save,
    User,
    Mail,
    Calendar,
    Hash,
    List,
    Phone,
    Activity,
    Plus,
    AlertCircle,
    Camera,
    FileText,
    CheckCircle,
    Map,
    MapPin,
    Building2,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import Search from "@/components/Students/Search/Search.vue";
  import StatusFilter from "@/components/Students/StatusFilter/StatusFilter.vue";
  import YearPicker from "@/components/Students/PassOutFilter/YearPicker.vue";
  const store = useStore();

  const { showToast: showGlobalToast } = useGlobalToast();
  // 🔹 Props (receiving from parent)
  const props = defineProps({
    bannerGoClicked: {
      type: Boolean,
      default: false,
    },
  });
  const showAddModal = ref(false);
  const isAdding = ref(false);
  const generalError = ref("");
  const showToast = ref(false);

  const classes = computed(() => store.getters.allClasses || []);
  // Computed value for total students
  const TotalStudents = computed(
    () => store.getters.getOnboardingTotalStudents || 0,
  );
  const selectedClass = ref(null);
  const selectedYear = ref("");
  const searchText = ref("");
  const phoneRegex = /^[6-9]\d{9}$/;

  const emit = defineEmits(["selected-class", "search-text", "selected-year"]);

  function emitSelectedClass(cls) {
    selectedClass.value = cls;
    // emit("selected-class", cls?._id || null);
    emit("selected-class", cls || null);
  }
  // check if passout
  const isPassoutClass = computed(() => {
    if (!selectedClass.value) return false;
    return selectedClass.value.name?.toLowerCase() === "passout";
  });
  function emitSelectedYear(year) {
    if (isPassoutClass.value) {
      selectedYear.value = year;
      emit("selected-year", year);
    } else {
      // 🚫 Do not send year if not passout
      selectedYear.value = "";
      emit("selected-year", "");
    }
  }

  // 🔹 Watch for class change → reset year if not passout
  watch(isPassoutClass, (val) => {
    if (!val) {
      selectedYear.value = "";
      emit("selected-year", "");
    }
  });
  watch(selectedClass, () => {
    emitSearchText("");
    searchText.value = "";
  });

  const formErrors = ref({
    name: "",
    rollNumber: "",
    email: "",
    phone: "",
    secondaryPhone: "",
    class: "",
    state: "", // optional
    district: "", // ✅ optional (added)
    city: "", // optional
    pincode: "", // 🔴 mandatory
    status: "",
    enrollmentDate: "",
    dob: "",
    photo: "",
    aadhaarCard: "",
    birthCertificate: "",
    transferCertificate: "",
    marksheet: "",
    session: "",
    year: "",
  });
  const newStudent = ref({
    rollNumber: "",
    name: "",
    email: "",
    class: "",
    phone: "",
    secondaryPhone: "",
    state: "", // optional
    district: "", // ✅ optional (added)
    city: "", // optional
    pincode: "", // 🔴 mandatory
    status: "active",
    enrollmentDate: "",
    photo: "",
    aadhaarCard: "",
    birthCertificate: "",
    transferCertificate: "",
    dob: "",
    marksheet: "",
    session: "",
    year: "",
  });

  const emitSearchText = (val) => emit("search-text", val);

  const closeToast = () => {
    showToast.value = false;
  };
  const closeAddModal = () => {
    showAddModal.value = false;
    newStudent.value = {
      rollNumber: "",
      name: "",
      email: "",
      class: "",
      phone: "",
      secondaryPhone: "",
      state: "", // optional
      district: "", // ✅ optional (added)
      city: "", // optional
      pincode: "", // 🔴 mandatory
      status: "",
      enrollmentDate: "",
      photo: "",
      dob: "",
      aadhaarCard: "",
      birthCertificate: "",
      transferCertificate: "",
      marksheet: "",
      session: "",
      year: "",
    };
    formErrors.value = {};
    generalError.value = "";
  };
  // ✅ Auto-generate email live from name
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      formErrors.value.photo = "Only image files are allowed.";
      return;
    }
    if (file.size > 1024 * 1024) {
      formErrors.value.photo = "Photo must be less than 1MB.";
      return;
    }
    newStudent.value.photo = file;
  };
  const handleDocumentUpload = (event, documentType) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      formErrors.value[documentType] = "Invalid file type.";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      formErrors.value[documentType] = "File must be under 2MB.";
      return;
    }
    newStudent.value[documentType] = file;
  };
  const refreshStudents = async () => {
    try {
      // Fetch the students
      await store.dispatch("fetchStudents", {
        page: 1,
        limit: 20,
        search: "",
      });

      // Fetch the total student count for onboarding
      await store.dispatch("fetchTotalStudentCountOnboarding");
    } catch (error) {
      console.error("❌ Error refreshing students:", error);
    }
  };

  const addStudent = async () => {
    formErrors.value = {};
    generalError.value = "";
    if (!newStudent.value.name?.trim()) {
      formErrors.value.name = "Student name is required";
      showGlobalToast("error", "Student name is required");
      return;
    }

    if (!newStudent.value.rollNumber?.trim()) {
      formErrors.value.rollNumber = "Roll number is required";
      showGlobalToast("error", "Roll number is required");
      return;
    }
    if (!newStudent.value.class) {
      formErrors.value.class = "Class selection is required";
      showGlobalToast("error", "Class selection is required");
      return;
    }

    const selectedClass = classes.value.find(
      (c) => c._id === newStudent.value.class,
    );
    const isPassoutClass = selectedClass?.name?.toLowerCase() === "passout";
    const isPassoutStatus = newStudent.value.status === "passout";

    if (isPassoutClass && !isPassoutStatus) {
      formErrors.value.status =
        "Status must be 'Passed Out' when class is 'Passout'";
      showGlobalToast(
        "error",
        "Status must be 'Passed Out' when class is 'Passout'",
      );
      return;
    }

    if (isPassoutStatus && !isPassoutClass) {
      formErrors.value.class =
        "Class must be 'Passout' when status is 'Passed Out'";
      showGlobalToast(
        "error",
        "Class must be 'Passout' when status is 'Passed Out'",
      );
      return;
    }
    // ✅ Primary phone validation
    if (!newStudent.value.phone?.trim()) {
      formErrors.value.phone = "Primary phone number is required";
      showGlobalToast("error", "Primary phone number is required");
      return;
    }

    if (!phoneRegex.test(newStudent.value.phone)) {
      formErrors.value.phone = "Enter a valid 10-digit mobile number";
      showGlobalToast("error", "Enter a valid primary mobile number");
      return;
    }

    // ✅ Secondary phone validation (optional)
    if (newStudent.value.secondaryPhone) {
      if (!phoneRegex.test(newStudent.value.secondaryPhone)) {
        formErrors.value.secondaryPhone =
          "Enter a valid 10-digit secondary mobile number";
        showGlobalToast("error", "Invalid secondary mobile number");
        return;
      }

      if (newStudent.value.secondaryPhone === newStudent.value.phone) {
        formErrors.value.secondaryPhone =
          "Secondary phone cannot be same as primary phone";
        showGlobalToast(
          "error",
          "Secondary phone cannot be same as primary phone",
        );
        return;
      }
    }

    if (!/^\d{6}$/.test(newStudent.value.pincode)) {
      formErrors.value.pincode = "Valid 6-digit pincode is required";
      showGlobalToast("error", "Valid 6-digit pincode is required");
      return;
    }

    isAdding.value = true;
    try {
      const formData = new FormData();
      const trimmedName = newStudent.value.name.trim();
      let finalEmail = newStudent.value.email?.trim() || "";

      formData.append("name", trimmedName);
      formData.append("email", finalEmail);

      formData.append("role", "student");
      formData.append("phone", newStudent.value.phone || "");
      if (newStudent.value.secondaryPhone) {
        formData.append(
          "secondaryPhone",
          newStudent.value.secondaryPhone.trim(),
        );
      }
      formData.append("class", newStudent.value.class);
      formData.append("rollNumber", newStudent.value.rollNumber.trim());

      formData.append("status", newStudent.value.status || "active");
      formData.append(
        "enrollmentDate",
        newStudent.value.enrollmentDate || new Date().toISOString(),
      );
      formData.append("dob", newStudent.value.dob || null);

      if (newStudent.value.photo instanceof File) {
        formData.append("photo", newStudent.value.photo);
      }
      if (newStudent.value.aadhaarCard instanceof File) {
        formData.append("aadhaarCard", newStudent.value.aadhaarCard);
      }
      if (newStudent.value.birthCertificate instanceof File) {
        formData.append("birthCertificate", newStudent.value.birthCertificate);
      }
      if (newStudent.value.transferCertificate instanceof File) {
        formData.append(
          "transferCertificate",
          newStudent.value.transferCertificate,
        );
      }
      if (newStudent.value.marksheet instanceof File) {
        formData.append("marksheet", newStudent.value.marksheet);
      }
      formData.append("pincode", newStudent.value.pincode);

      if (newStudent.value.state)
        formData.append("state", newStudent.value.state);

      if (newStudent.value.district)
        formData.append("subDistrict", newStudent.value.district);

      if (newStudent.value.city) formData.append("city", newStudent.value.city);

      if (isPassoutClass && newStudent.value.status === "passout") {
        if (newStudent.value.session) {
          formData.append("session", newStudent.value.session);
        }
        if (newStudent.value.year) {
          formData.append("passoutYear", newStudent.value.year);
        }
      }
      await store.dispatch("addStudent", formData);

      showGlobalToast("success", "Student added successfully");
      await refreshStudents();

      closeAddModal();
    } catch (error) {
      console.error("Add student error:", error);
      if (error.response?.data?.errors) {
        formErrors.value = error.response.data.errors;
        const firstErrorKey = Object.keys(formErrors.value)[0];
        const firstErrorMessage = formErrors.value[firstErrorKey];
        showGlobalToast("error", firstErrorMessage);
      } else {
        const message = error.response?.data?.message || "Server error.";
        generalError.value = message;
        showGlobalToast("error", message);
      }
    } finally {
      isAdding.value = false;
    }
  };

  // Determine the class selected in the form and enforce passout status behavior
  const selectedFormClass = computed(() => {
    return classes.value.find((c) => c._id === newStudent.value.class) || null;
  });
  const isPassoutSelectedInForm = computed(() => {
    return selectedFormClass.value?.name?.toLowerCase() === "passout";
  });
  watch(isPassoutSelectedInForm, (val) => {
    if (val) {
      newStudent.value.status = "passout";
    }
  });
  watch(
    () => newStudent.value.status,
    (val) => {
      if (val === "passout") {
        const passoutClass = classes.value.find(
          (c) => c.name?.toLowerCase() === "passout",
        );
        if (passoutClass) {
          newStudent.value.class = passoutClass._id;
        }
      }
    },
  );

  onMounted(() => {
    store.dispatch("fetchAllClasses");
  });
  // Watcher to react immediately when TotalStudents is 0
  watch(
    TotalStudents,
    (newVal) => {
      if (newVal === 0) {
        showAddModal.value = true;
      }
    },
    { immediate: true }, // <- triggers the watcher immediately on component mount
  );
  const filteredClasses = computed(() => {
    return classes.value.filter((cls) => {
      // Hide "Passout" class if no students exist
      if (TotalStudents.value === 0 && cls.name?.toLowerCase() === "passout") {
        return false;
      }
      return true;
    });
  });
  const filteredStatuses = computed(() => {
    const statuses = [
      { label: "Active", value: "active" },
      // { label: "InActive", value: "leaved" },
    ];

    // Only add "Passed Out" if TotalStudents > 0
    if (TotalStudents.value > 0) {
      statuses.push({ label: "Passed Out", value: "passout" });
    }

    return statuses;
  });
  // 👀 Watch for the prop change
  watch(
    () => props.bannerGoClicked,
    (newVal) => {
      if (newVal) {
        showAddModal.value = true;
      }
    },
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
  /* Custom animations */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  /* Custom scrollbar for modal */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }
  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  /* Disabled state improvements */
  .disabled\:opacity-50:disabled {
    opacity: 0.5;
  }
  .disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
  }
  /* Toast animations */
  .translate-x-full {
    transform: translateX(100%);
  }
  .translate-x-0 {
    transform: translateX(0);
  }
</style>
